/**
 * 登录状态与门店上下文
 * ---------------------------------------------------------------------------
 * 全应用只有这一个地方持有"我是谁、我在哪家门店、我能做什么"。
 * 页面通过 useAuth() 取用，不要自己去读 localStorage。
 *
 * 权限口径（产品文档 §8.1）：
 *   前端隐藏按钮只是体验优化，真正的拦截在服务端。
 *   所以 can() 返回 false 时按钮消失，但即使被绕过，接口也会返回 403。
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi, type LoginInput } from '@/lib/api/endpoints/auth';
import { storesApi } from '@/lib/api/endpoints/stores';
import { setUnauthorizedHandler } from '@/lib/api/http';
import type { AuthContext as AuthContextData, Store } from '@/lib/api/types';
import { AuthContext, type AuthState } from '@/lib/auth/auth-store';
import {
  clearSession,
  readSession,
  resolveExpiresAt,
  updateStoredStoreId,
  writeSession,
} from '@/lib/auth/session-storage';

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const queryClient = useQueryClient();
  const [initializing, setInitializing] = useState(true);
  const [user, setUser] = useState<AuthContextData | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [stores, setStores] = useState<Store[]>([]);
  const [currentStoreId, setCurrentStoreId] = useState<string | null>(null);

  /**
   * 退出登录：清本地态，路由层会自动把用户送回登录页。
   *
   * 必须同时清空 React Query 缓存。QueryClient 是应用启动时建的单例，
   * 不清的话缓存会跨账号留存：门店前台和员工手机端常常是同一台设备轮流用，
   * 下一个人登录后，在后台重新请求回来之前会先看到上一个人的顾客名单和提成
   * 金额——这正是产品文档 §8.10「员工离职或停用后立即失去访问权限」要防的事。
   */
  const logout = useCallback(() => {
    clearSession();
    setUser(null);
    setDisplayName('');
    setStores([]);
    setCurrentStoreId(null);
    queryClient.clear();
  }, [queryClient]);

  /**
   * 登录成功后拉取门店列表并确定当前门店。
   * 抽出来是因为"刚登录"和"带着本地 token 启动"两条路径都要走这一步。
   */
  const loadStores = useCallback(async (preferredStoreId: string | null) => {
    const list = await storesApi.list();
    setStores(list);
    // 优先用本地记住的门店；对不上（比如被取消授权了）就退回第一家
    const matched = list.find((store) => store.id === preferredStoreId);
    const resolved = matched ?? list[0] ?? null;
    setCurrentStoreId(resolved?.id ?? null);
    if (resolved) updateStoredStoreId(resolved.id);
  }, []);

  // token 失效时（比如后台改了权限、8 小时到期）由 http 层通知这里
  useEffect(() => {
    setUnauthorizedHandler(() => logout());
    return () => setUnauthorizedHandler(null);
  }, [logout]);

  // 启动时用本地 token 换一次 /me，确认登录态是否还有效
  useEffect(() => {
    const session = readSession();
    if (!session) {
      setInitializing(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const context = await authApi.me();
        if (cancelled) return;
        setUser(context);
        setDisplayName(session.displayName);
        if (!context.passwordChangeRequired) {
          await loadStores(session.storeId);
        }
      } catch {
        // token 失效或后端不可用，都按未登录处理，让用户重新登录
        clearSession();
      } finally {
        if (!cancelled) setInitializing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadStores]);

  const login = useCallback(
    async (input: LoginInput) => {
      const result = await authApi.login(input);
      writeSession({
        accessToken: result.accessToken,
        expiresAt: resolveExpiresAt(result.expiresIn),
        merchantCode: input.merchantCode,
        displayName: result.user.displayName,
        storeId: result.user.storeIds[0] ?? null,
      });
      /**
       * **整体展开，不要逐个挑字段。**
       *
       * 原来这里是一份手抄的字段清单，服务端每加一个字段就要记得同步一次，
       * 而漏掉不会立刻出错——只会让刚登录那一瞬间的 user 少一个字段，
       * 刷新页面走 `/me` 又好了，症状飘忽得几乎查不到。
       * 实际就漏过 `canAccessConsole`：老板登录后被管理端围栏当成员工踢去手机端，
       * 刷新一下却又正常（2026-08-05 罗伯特的账号）。
       *
       * 展开之后，服务端加字段时前端自动带上；真有对不上的地方 `tsc -b` 会报错。
       * `id` 单独映射成 `userId` 是登录响应和 `/me` 之间唯一的形状差异。
       */
      const { id, displayName: _displayName, ...context } = result.user;
      setUser({ userId: id, ...context });
      setDisplayName(result.user.displayName);
      if (!result.user.passwordChangeRequired) {
        await loadStores(result.user.storeIds[0] ?? null);
      }
    },
    [loadStores],
  );

  const switchStore = useCallback((storeId: string) => {
    setCurrentStoreId(storeId);
    updateStoredStoreId(storeId);
  }, []);

  // 权限码用 Set 存，避免每次渲染都在数组里线性查找
  const permissionSet = useMemo(
    () => new Set(user?.permissionCodes ?? []),
    [user],
  );

  const can = useCallback(
    (permissionCode: string) => permissionSet.has(permissionCode),
    [permissionSet],
  );

  const currentStore = useMemo(
    () => stores.find((store) => store.id === currentStoreId) ?? null,
    [stores, currentStoreId],
  );

  const value = useMemo<AuthState>(
    () => ({
      initializing,
      user,
      displayName,
      stores,
      currentStore,
      login,
      logout,
      switchStore,
      can,
    }),
    [
      initializing,
      user,
      displayName,
      stores,
      currentStore,
      login,
      logout,
      switchStore,
      can,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
